import { useEffect, useState } from 'react';
import { useFormApi } from 'informed';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getHomePageModulesByType } from 'lib/homepage';
import { LoadingIndicator } from 'components/common';
import {
  HomePageSettingsModulesAdd,
  HomePageSettingsModulesControls,
  HomePageSettingsModulesModule
} from '.';

import type { GetHomePageModulesRes } from 'shared-types/responses';
import type { HomePageSettingsFormValues } from './HomePageSettings';

export type ModuleInSettings = GetHomePageModulesRes['modules'][0];

const HomePageSettingsModules = () => {
  const { dispatch } = useDispatch();
  const { messages, serverConfig } = useReduxState();

  const formApi = useFormApi<HomePageSettingsFormValues>();

  const [modules, setModules] = useState<ModuleInSettings[]>([]);

  const { data, loading } = useApiData<GetHomePageModulesRes>({
    url: '/admin/home-page'
  });

  const modulesByType = getHomePageModulesByType({ messages, serverConfig });

  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  useEffect(() => {
    if (data?.modules?.length) {
      setModules(data.modules);
      formApi.setValue('homePageModules', data.modules);
      dispatch(actions.updatePageState({ refreshInitialState: true }));
    }
  }, [data]);

  const isModuleVisible = (module: ModuleInSettings) =>
    module.type in modulesByType;

  const visibleIndexes = modules.flatMap((module, index) =>
    isModuleVisible(module) ? [index] : []
  );
  const firstVisibleIndex = visibleIndexes[0];
  const lastVisibleIndex = visibleIndexes[visibleIndexes.length - 1];

  const copyModules = (): ModuleInSettings[] =>
    JSON.parse(JSON.stringify(modules));

  const handleAddModule = (newModule: ModuleInSettings) => {
    setModules([...modules, newModule]);
    formApi.setValue('homePageModules', [...modules, newModule]);
  };

  const handleMoveModule = (index: number, direction: 1 | -1) => {
    const visiblePosition = visibleIndexes.indexOf(index);
    if (visiblePosition < 0) return;

    const targetPosition = visiblePosition + direction;
    if (targetPosition < 0 || targetPosition >= visibleIndexes.length) {
      return;
    }

    const targetIndex = visibleIndexes[targetPosition];

    const nextModules = copyModules();

    const movedModule = nextModules.splice(index, 1)[0];
    nextModules.splice(targetIndex, 0, movedModule);

    setModules(nextModules);
    formApi.setValue('homePageModules', nextModules);
  };

  const handleRemoveModule = (index: number) => {
    if (index < 0) return;

    const modules = copyModules();
    modules.splice(index, 1);

    setModules(modules);
    formApi.setValue('homePageModules', modules);
  };

  const handleUpdateModule = (index: number, newModule: ModuleInSettings) => {
    if (index < 0) return;

    const modules = copyModules();
    modules.splice(index, 1, newModule);

    setModules(modules);
    formApi.setValue('homePageModules', modules);
  };

  return loading ? (
    <LoadingIndicator />
  ) : (
    <div className="modules">
      {coursesVisible && (
        <div className="module">
          <HomePageSettingsModulesModule
            index={-2}
            module={{
              data: {},
              description: '',
              descriptionTranslations: {},
              limit: 4,
              type: 'active-courses',
              title: '',
              titleTranslations: {}
            }}
            updateModule={handleUpdateModule}
          />

          <HomePageSettingsModulesControls
            index={-1}
            locked={true}
            move={handleMoveModule}
            remove={handleRemoveModule}
          />
        </div>
      )}

      {pathsVisible && (
        <div className="module">
          <HomePageSettingsModulesModule
            index={-1}
            module={{
              data: {},
              description: '',
              descriptionTranslations: {},
              limit: 4,
              type: 'active-paths',
              title: '',
              titleTranslations: {}
            }}
            updateModule={handleUpdateModule}
          />

          <HomePageSettingsModulesControls
            index={-1}
            locked={true}
            move={handleMoveModule}
            remove={handleRemoveModule}
          />
        </div>
      )}

      {modules.map((module, index) =>
        // Filter out disabled modules with a ternary instead of filter(), because
        // handlers rely on indexes to update, remove, and validate the correct module
        isModuleVisible(module) ? (
          <div className="module" key={index}>
            <HomePageSettingsModulesModule
              index={index}
              module={module}
              updateModule={handleUpdateModule}
            />

            <HomePageSettingsModulesControls
              first={index === firstVisibleIndex}
              index={index}
              last={index === lastVisibleIndex}
              move={handleMoveModule}
              remove={handleRemoveModule}
            />
          </div>
        ) : null
      )}

      <div className="add">
        <HomePageSettingsModulesAdd
          addedTypes={modules.map((module) => module.type)}
          addModule={handleAddModule}
        />
      </div>

      <style jsx>{`
        .modules {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
        .module {
          display: flex;
          flex-direction: row;
          gap: 20px;
          padding: 0;
          width: 100%;
        }
        .add {
          padding-right: 54px;
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsModules;
